/* Single confirmed-cart authority. No optimistic prices; every write is serialized. */
(() => {
  if (globalThis.DHCartCore) return;
  const planId = item => item.selling_plan_allocation?.selling_plan?.id ?? null;
  const signature = item => JSON.stringify([String(item.variant_id || item.id),planId(item),Object.entries(item.properties || {}).sort(([a],[b])=>a.localeCompare(b))]);
  function resolve(items, snapshot) {
    const exact = items.find(i=>i.key===snapshot.key);
    if (exact) return exact;
    const matches = items.filter(i=>signature(i)===signature(snapshot));
    if (matches.length !== 1) throw new Error('Řádek se změnil. Obnovte košík a zkontrolujte množství.');
    return matches[0];
  }
  const metadata = (state,item) => state.catalog?.find(p=>String(p.id)===String(item.product_id)) || {category:'other',units:0,variants:[],plans:[]};
  const maskUnits = state => state.cart.items.reduce((n,i)=>{const p=metadata(state,i);const eligible=!state.config?.commerce?.enabled||state.config.commerce.productIds.includes(Number(i.product_id));return n+(eligible&&p.category==='mask' && Number.isInteger(p.units) && p.units>0 ? i.quantity*p.units : 0);},0);
  const variant = (state,item) => metadata(state,item).variants?.find(v=>String(v.id)===String(item.variant_id || item.id));
  function availablePlans(state,item) {
    const p=metadata(state,item),v=variant(state,item);
    return (p.plans||[]).filter(plan=>v?.allocations?.some(a=>String(a.id)===String(plan.id)));
  }
  function phases(state,item,id,money) {
    const p=metadata(state,item),plan=p.plans?.find(p=>String(p.id)===String(id));
    const allocation=variant(state,item)?.allocations?.find(a=>String(a.id)===String(id));
    if (!plan || !allocation) return 'Ceny dalších zásilek je nutné ověřit v podmínkách předplatného.';
    let previous=0;
    const parts=(plan.adjustments||[]).map((phase,i)=>{
      const price=allocation.adjustments?.[i]?.price;
      const count=phase.order_count;
      const label=i===0?(count===1?'První zásilka':count?`První ${count} zásilky`:'Každá zásilka'):`Od ${previous+1}. zásilky`;
      previous+=count||0;
      return Number.isFinite(price)?`${label}: ${money(price*item.quantity)}`:null;
    }).filter(Boolean);
    return (parts.length?parts.join('; '):`Za zásilku: ${money(allocation.price*item.quantity)}`)+'. Bez dopravy a případných dalších slev.';
  }
  class Store extends EventTarget {
    constructor(adapter) {
      super();this.adapter=adapter;this.state=null;this.pending=0;this.error='';this.uncertain=false;this.chain=Promise.resolve();this.quantities=new Map();this.adds=new Map();this.epoch=0;
    }
    emit(){this.dispatchEvent(new Event('change'));}
    get busy(){return this.pending>0;}
    get canCheckout(){return !!this.state?.cart.items.length && !this.busy && !this.error && !this.uncertain && !this.state.cart.items.some(i=>variant(this.state,i)?.available===false);}
    commit(state){
      if (!state || !Array.isArray(state.cart?.items) || !Number.isSafeInteger(state.cart.total_price)) throw new Error('Košík se nepodařilo načíst. Zkuste obnovit jeho stav.');
      this.state=state;this.emit();
    }
    enqueue(operation,{mutation=false}={}) {
      const epoch=this.epoch;this.pending++;this.emit();
      const task=this.chain.catch(()=>{}).then(async()=>{
        if(epoch!==this.epoch) throw new Error('Navazující změna byla zrušena. Zkontrolujte košík.');
        if(mutation&&(this.uncertain||this.error)) throw new Error('Nejdříve obnovte a zkontrolujte košík.');
        try { return await operation(); }
        catch(error){
          this.epoch++;this.error=error.message||'Změnu se nepodařilo potvrdit.';
          if(mutation) {
            this.uncertain=true;
            try{this.commit(await this.adapter.read());}catch{this.error+=' Aktuální stav se nepodařilo ověřit.';}
          }
          this.emit();throw error;
        }
      }).finally(()=>{this.pending--;this.emit();});
      this.chain=task;return task;
    }
    refresh(){return this.enqueue(async()=>{const state=await this.adapter.read();this.error='';this.uncertain=false;this.commit(state);return state;});}
    add(payload) {
      const key=payload instanceof FormData ? 'form:'+JSON.stringify([...payload.entries()]) : JSON.stringify(payload);
      if(this.adds.has(key)) return this.adds.get(key);
      const task=this.enqueue(async()=>{
        if(!this.state)this.commit(await this.adapter.read());
        const requested=payload instanceof FormData?null:payload.items;
        const expected=new Map();
        for(const input of requested||[]){
          const sig=signature({variant_id:input.id,properties:input.properties||{},selling_plan_allocation:input.selling_plan?{selling_plan:{id:input.selling_plan}}:null});
          const before=this.state.cart.items.filter(i=>signature(i)===sig).reduce((n,i)=>n+i.quantity,0);
          expected.set(sig,(expected.get(sig)??before)+input.quantity);
        }
        await this.adapter.add(payload);this.commit(await this.adapter.read());
        for(const [sig,qty] of expected)if(this.state.cart.items.filter(i=>signature(i)===sig).reduce((n,i)=>n+i.quantity,0)!==qty)throw new Error('Potvrzený obsah se liší od požadovaného přidání. Zkontrolujte košík.');
        return this.state;
      },{mutation:true}).finally(()=>this.adds.delete(key));
      this.adds.set(key,task);return task;
    }
    desired(item){return this.quantities.get(signature(item))?.desired ?? item.quantity;}
    quantity(snapshot,desired) {
      const sig=signature(snapshot),existing=this.quantities.get(sig);
      if(existing){existing.desired=Math.max(0,Math.floor(desired));this.emit();return existing.promise;}
      const entry={desired:Math.max(0,Math.floor(desired)),snapshot:structuredClone(snapshot)};
      this.quantities.set(sig,entry);
      entry.promise=this.enqueue(async()=>{
        // Let rapid controls settle before issuing the first request.
        await new Promise(r=>setTimeout(r,120));
        while(true){
          const row=resolve(this.state.cart.items,entry.snapshot),target=entry.desired;
          await this.adapter.change({id:row.key,quantity:target});
          this.commit(await this.adapter.read());
          if(target===0){if(this.state.cart.items.some(i=>i.key===row.key))throw new Error('Odstranění položky nebylo potvrzené. Zkontrolujte košík.');break;}
          entry.snapshot=resolve(this.state.cart.items,row);
          if(entry.snapshot.quantity!==target) throw new Error('Dostupné množství se změnilo. Zkontrolujte potvrzený počet balení.');
          if(entry.desired===target)break;
        }
      },{mutation:true}).finally(()=>{this.quantities.delete(sig);this.emit();});
      return entry.promise;
    }
    setPlans(snapshots,id) {
      return this.enqueue(async()=>{
        let changed=0;
        try{
          for(const snapshot of snapshots){
            const row=resolve(this.state.cart.items,snapshot);
            if(id!==null&&!availablePlans(this.state,row).some(p=>String(p.id)===String(id)))throw new Error('Zvolený plán již není pro tuto položku dostupný.');
            if(id===null&&variant(this.state,row)?.requiresPlan)throw new Error('Tato položka vyžaduje předplatné.');
            const line=this.state.cart.items.indexOf(row)+1;
            const targetSignature=signature({...row,selling_plan_allocation:id===null?null:{selling_plan:{id}}});
            const expectedTarget=this.state.cart.items.filter(i=>signature(i)===targetSignature).reduce((n,i)=>n+i.quantity,0)+(String(planId(row))===String(id)?0:row.quantity);
            await this.adapter.change({line,quantity:row.quantity,selling_plan:id});
            this.commit(await this.adapter.read());
            const actualTarget=this.state.cart.items.filter(i=>signature(i)===targetSignature).reduce((n,i)=>n+i.quantity,0);
            if(actualTarget!==expectedTarget)throw new Error('Server nepotvrdil celý převod na zvolený plán.');
            changed++;
          }
        }catch(e){throw new Error(`${changed?`Potvrzeno ${changed} z ${snapshots.length} změn. `:''}${e.message} Zkontrolujte režim u jednotlivých položek.`);}
      },{mutation:true});
    }
  }
  globalThis.DHCartCore={Store,planId,signature,resolve,metadata,variant,maskUnits,availablePlans,phases};
})();
